import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Divider, Link, Stack, Typography } from "@mui/material";
import { AccessTime, Pause as PauseIcon, Replay, ViewAgendaOutlined } from "@mui/icons-material";
import { ProfileInterface, UserProfileRole } from "ts-interfaces";
import * as Sentry from "@sentry/react";
import { reportBillingRefusal } from "./billingRefusal";
import { AuthContext } from "../auth/Auth";
import { useLoginState } from "../studioxr/auth/LoginState";
import { useMattoState } from "../MattoState";
import { fetchData } from "../libs/util.ts";
import { trackEvent } from "../libs/analyticsManager.ts";
import { PrimaryActionButton } from "../design_stream/components/shared/buttons.styles";
import { pauseStripeSubscriptionAPIUrl, resumeStripeSubscriptionAPIUrl } from "./config.js";
import { useAccountSummary } from "./useAccountSummary";

// Pause instead of cancel (issue #3957). Pausing turns Pro off at once and
// keeps the subscription; the webhook stamps profiles.billingState: paused and
// restores Pro when Stripe lifts the pause. Dates come from the server
// (/getSubscription → pauseOptions), never from client-side date maths.

/** One pause length offered by /getSubscription. Epoch seconds. */
export interface PauseOption {
  months: number;
  resumesAt: number;
  nextChargeAt: number;
}

type TokenUser = { getIdToken: () => Promise<string> } | null;

const ACCENT = "linear-gradient(89.95deg, #5B47F5 1.57%, #B782FF 50.91%, #D0B1FE 98.39%)";
const MUTED = "#6B6B6B";

/** Epoch seconds, a Firestore Timestamp or a Date → "November 1, 2026", or "Nov 1, 2026" when short (null when unknown). */
export const formatPauseDate = (value: unknown, month: "long" | "short" = "long"): string | null => {
  const date =
    typeof value === "number"
      ? new Date(value * 1000)
      : ((value as { toDate?: () => Date })?.toDate?.() ?? (value instanceof Date ? value : null));
  // UTC, as in the pause email.
  return date ? date.toLocaleDateString("en-US", { month, day: "numeric", year: "numeric", timeZone: "UTC" }) : null;
};

// The backend resolves the subscription from the id token.
const postAsUser = async (url: string, body: object, currentUser: TokenUser) => {
  if (!currentUser) throw new Error("Not signed in");
  return fetchData(body, url, { Authorization: `Bearer ${await currentUser.getIdToken()}` }, { rethrow: true });
};

const durationSx = (selected: boolean) =>
  ({
    flex: 1,
    minWidth: 0,
    height: 44,
    borderRadius: "10px",
    textTransform: "none",
    padding: "0 4px",
    whiteSpace: "nowrap",
    fontSize: { xs: "14px", sm: "15px" },
    fontWeight: 500,
    border: "1px solid",
    borderColor: selected ? "#1A1A1A" : "#E0E0E0",
    background: selected ? "#1A1A1A" : "#fff",
    color: selected ? "#fff" : "#1A1A1A",
    "&:hover": { background: selected ? "#1A1A1A" : "#F5F5F5", borderColor: selected ? "#1A1A1A" : "#BDBDBD" },
  }) as const;

const KEEPS = [
  { icon: <ViewAgendaOutlined fontSize="small" />, label: "Keep your boards" },
  { icon: <AccessTime fontSize="small" />, label: "Keep saved materials" },
  { icon: <Replay fontSize="small" />, label: "Come back anytime" },
];

interface PauseOfferProps {
  options: PauseOption[];
  /** Current billing period start (epoch seconds). */
  periodStart?: number | null;
  onContinueToCancel: () => void;
  onClose: () => void;
}

/** "12 boards · 37 credits · 5 concepts this cycle": what the account holds, above the options. */
const AccountSummaryRow = ({ periodStart }: { periodStart?: number | null }) => {
  const uid = useLoginState((state) => (state.profile as ProfileInterface | null)?.uid);
  const summary = useAccountSummary(uid, periodStart);
  const stats = [
    { value: summary.boards, label: summary.boards === 1 ? "board" : "boards" },
    { value: summary.credits, label: summary.credits === 1 ? "credit left" : "credits left" },
    { value: summary.concepts, label: summary.concepts === 1 ? "concept this cycle" : "concepts this cycle" },
  ].filter((stat) => typeof stat.value === "number");
  if (!stats.length) return null;
  return (
    <Stack direction="row" role="list" aria-label="Your account" spacing={1} sx={{ mt: { xs: 1.5, md: 2 } }}>
      {stats.map((stat) => (
        <Stack key={stat.label} role="listitem" sx={{ alignItems: "center", flex: 1, minWidth: 0, py: 1, borderRadius: "10px", border: "1px solid #E0E0E0", background: "#FAFAFA" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, lineHeight: 1.2 }}>{stat.value}</Typography>
          <Typography sx={{ color: MUTED, fontSize: { xs: "12px", sm: "13px" }, textAlign: "center" }}>{stat.label}</Typography>
        </Stack>
      ))}
    </Stack>
  );
};

/** The first screen of the cancel flow for a subscription that can pause. */
export const PauseOffer = ({ options, periodStart = null, onContinueToCancel, onClose }: PauseOfferProps) => {
  const { currentUser } = useContext(AuthContext) as { currentUser: TokenUser };
  const profile = useLoginState((state) => state.profile) as ProfileInterface | null;
  const setProfile = useLoginState((state) => state.setProfile);
  const [months, setMonths] = useState(options[0].months);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState<PauseOption | null>(null);

  useEffect(() => {
    trackEvent("Pause Offer Shown");
  }, []);

  const selected = options.find((option) => option.months === months) ?? options[0];
  const returnsOn = formatPauseDate((paused ?? selected).resumesAt, paused ? "long" : "short");
  const nextChargeOn = formatPauseDate((paused ?? selected).nextChargeAt, paused ? "long" : "short");

  const pause = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await postAsUser(pauseStripeSubscriptionAPIUrl, { months: selected.months }, currentUser);
      const confirmed: PauseOption = {
        months: selected.months,
        resumesAt: result?.resumesAt ?? selected.resumesAt,
        nextChargeAt: result?.nextChargeAt ?? selected.nextChargeAt,
      };
      trackEvent("Subscription Paused", { months: confirmed.months });
      // The webhook writes the same fields; this keeps the UI from lagging it.
      setProfile({
        ...profile,
        userRole: UserProfileRole.BASIC,
        billingState: "paused",
        billingResumesAt: new Date(confirmed.resumesAt * 1000),
      });
      setPaused(confirmed);
    } catch (err) {
      reportBillingRefusal("pauseStripeSubscription", err);
      Sentry.captureException(err);
      setError("We couldn't pause your membership. Please try again or contact studio@mattoboard.com.");
      setBusy(false);
    }
  };

  if (paused) {
    return (
      <Stack spacing={2} sx={{ width: "100%", height: "100%", justifyContent: "safe center", boxSizing: "border-box", overflowY: "auto", p: { xs: 3, md: 5 } }}>
        <Typography sx={{ fontSize: { xs: "24px", md: "28px" }, fontWeight: 700, lineHeight: 1.2 }}>Your membership is paused</Typography>
        <Typography sx={{ color: MUTED, fontSize: "16px", lineHeight: 1.5 }}>
          Pro is off until {returnsOn}, and you won't be charged before {nextChargeOn}. You can resume any time from Accounts &amp; Billing.
        </Typography>
        <PrimaryActionButton fullWidth onClick={onClose} sx={{ height: 52, borderRadius: "14px" }}>
          Done
        </PrimaryActionButton>
      </Stack>
    );
  }

  return (
    <Stack sx={{ width: "100%", height: "100%", boxSizing: "border-box", overflowY: "auto", p: { xs: 2.5, md: 5 }, pt: { xs: 3, md: 6 } }}>
      <Typography sx={{ fontSize: { xs: "22px", md: "28px" }, fontWeight: 700, lineHeight: 1.2, pr: 4 }}>
        Taking a break between projects?
      </Typography>
      <Typography sx={{ color: MUTED, fontSize: { xs: "14px", md: "16px" }, lineHeight: 1.5, mt: { xs: 1, md: 1.5 } }}>
        Pause your membership and return whenever your next project begins. Your boards and saved materials will be here when you come back.
      </Typography>
      <AccountSummaryRow periodStart={periodStart} />

      <Box
        sx={{
          mt: { xs: 2, md: 3 },
          p: { xs: 1.5, md: 2.5 },
          borderRadius: "16px",
          border: "1.5px solid transparent",
          background: `linear-gradient(#F8F6FF, #F8F6FF) padding-box, ${ACCENT} border-box`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: "10px", background: ACCENT, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PauseIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ columnGap: 1, rowGap: 0.5 }}>
              <Typography sx={{ fontSize: "17px", fontWeight: 700 }}>Pause membership</Typography>
              <Box sx={{ border: "1px solid #8B6CF6", color: "#7A5AF5", fontSize: "12px", fontWeight: 600, padding: "2px 10px", borderRadius: "999px" }}>
                Recommended
              </Box>
            </Stack>
            <Typography sx={{ color: MUTED, fontSize: "14px", mt: 0.25 }}>Your account stays exactly as you left it.</Typography>
          </Box>
          {/* The only option on this screen: a decorative "selected" mark. */}
          <Box aria-hidden sx={{ width: 20, height: 20, flexShrink: 0, borderRadius: "50%", border: "2px solid #7A5AF5", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: ACCENT }} />
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} role="group" aria-label="Pause length" sx={{ mt: { xs: 1.5, md: 2 } }}>
          {options.map((option) => (
            <Button
              key={option.months}
              disableElevation
              aria-pressed={option.months === selected.months}
              onClick={() => setMonths(option.months)}
              disabled={busy}
              sx={durationSx(option.months === selected.months)}
            >
              {option.months === 1 ? "1 month" : `${option.months} months`}
            </Button>
          ))}
        </Stack>

        <Typography sx={{ color: MUTED, fontSize: "13px", textAlign: "center", mt: { xs: 1, md: 1.5 }, lineHeight: 1.5 }}>
          Pro pauses today and returns on {returnsOn}. Your next charge is on {nextChargeOn}.
          <br />
          You won't be charged while paused. Reactivate anytime.
        </Typography>
      </Box>

      <PrimaryActionButton fullWidth onClick={pause} disabled={busy} sx={{ mt: { xs: 2, md: 2.5 }, height: { xs: 48, md: 52 }, borderRadius: "14px", flexShrink: 0 }}>
        {busy ? "Pausing…" : "Pause my membership"}
      </PrimaryActionButton>
      {error && (
        <Typography role="alert" variant="body2" sx={{ color: "#cc010c", textAlign: "center", mt: 1 }}>
          {error}
        </Typography>
      )}
      <Link
        component="button"
        type="button"
        onClick={onContinueToCancel}
        disabled={busy}
        sx={{ alignSelf: "center", mt: 1.5, color: MUTED, textDecorationColor: MUTED, fontSize: "15px" }}
      >
        Continue to cancel
      </Link>

      <Divider sx={{ mt: { xs: 2, md: 3 }, mb: { xs: 2, md: 2.5 } }} />
      <Stack direction="row" justifyContent="space-around" spacing={1}>
        {KEEPS.map((keep) => (
          <Stack key={keep.label} alignItems="center" spacing={0.75} sx={{ flex: 1, minWidth: 0, color: MUTED }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", border: "1px solid #E0E0E0", background: "#FAFAFA", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {keep.icon}
            </Box>
            <Typography sx={{ fontSize: { xs: "12px", sm: "13px" }, textAlign: "center" }}>{keep.label}</Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

interface PausedPanelProps {
  /** Stripe's resumes_at (epoch seconds) or the profile's billingResumesAt. */
  resumesAt?: unknown;
  /** Called once the subscription is resumed, e.g. to re-read the live plan. */
  onResumed?: () => void;
}

/** "Paused until <date>" with Resume now: the account modal's plan panel, and what a paused member gets instead of checkout. */
export const PausedPanel = ({ resumesAt, onResumed }: PausedPanelProps) => {
  const { currentUser } = useContext(AuthContext) as { currentUser: TokenUser };
  const profile = useLoginState((state) => state.profile) as ProfileInterface | null;
  const setProfile = useLoginState((state) => state.setProfile);
  const setShowSubscription = useMattoState((state) => state.setShowSubscription);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const until = formatPauseDate(resumesAt);

  const resume = async () => {
    setBusy(true);
    setError(null);
    try {
      await postAsUser(resumeStripeSubscriptionAPIUrl, {}, currentUser);
      trackEvent("Subscription Resumed");
      setProfile({ ...profile, userRole: UserProfileRole.PRO, billingState: null, billingResumesAt: null });
      onResumed?.();
    } catch (err) {
      reportBillingRefusal("resumeStripeSubscription", err);
      Sentry.captureException(err);
      setError("We couldn't resume your membership. Please try again or contact studio@mattoboard.com.");
      setBusy(false);
    }
  };

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h5" component="h5">
        Mattoboard PRO Tier{" "}
        <Typography component="span" variant="h5" sx={{ color: "#7A5AF5", fontWeight: "bold" }}>
          — paused
        </Typography>
      </Typography>
      <Typography sx={{ paddingBottom: "10px" }}>
        {until ? `Paused until ${until}.` : "Your membership is paused."} Resuming brings Pro back today; billing continues on your next regular billing date.
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          disableElevation
          onClick={resume}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: "13px", padding: "4px 12px", borderRadius: "6px", background: "#000", color: "#fff", "&:hover": { background: "#222" } }}
        >
          {busy ? "Resuming…" : "Resume now"}
        </Button>
        <Link component="button" type="button" onClick={() => setShowSubscription("cancel")} disabled={busy} sx={{ color: MUTED, textDecorationColor: MUTED, fontSize: "13px" }}>
          Cancel membership
        </Link>
      </Stack>
      {error && (
        <Typography variant="body2" sx={{ color: "#cc010c" }}>
          {error}
        </Typography>
      )}
    </Stack>
  );
};
